import { create } from "zustand";
import { withLenses } from "@dhmk/zustand-lens";
import { consoleLogsSlice } from "./slices/console-logs.slice";
import { previewStateSlice } from "./slices/preview.slice";
import { workspaceStateSlice } from "./slices/workspace-state.slice";
import { sandboxStateSlice } from "./slices/sandbox-state.slice";
import { networkLogsSlice } from "./slices/network-logs.slice";
import { codeEditorSlice } from "./slices/code-editor.slice";
import { promptContextSlice } from "./slices/prompt-context.slice";
import { uiSlice } from "./slices/ui.slice";
import { chatSlice } from "./slices/chat.slice";
import { billingSlice } from "./slices/billing.slice";
import {
  createSelectors,
  createNestedSliceSelectors,
  type StoreWithNestedSlices,
} from "./create-selectors.util";
import { devtools } from "zustand/middleware";
import {
  type GetProjectResponseBody,
  type RefreshProjectRealtimeSubscriptionTokenResponseBody,
} from "@imagine/ai-service/shared-types/route-types";
import { ID } from "@appwrite.io/console";
import { imagineConfigStore } from "./imagine-config.store";

const slices = {
  consoleLogs: consoleLogsSlice,
  networkLogs: networkLogsSlice,
  sandboxState: sandboxStateSlice,
  previewState: previewStateSlice,
  workspaceState: workspaceStateSlice,
  codeEditor: codeEditorSlice,
  promptContext: promptContextSlice,
  chat: chatSlice,
  ui: uiSlice,
  billing: billingSlice,
} as const;

type ImagineStoreState = typeof slices & {
  projectId: string | null;
  project: GetProjectResponseBody | null;
  projectRealtimeSusbcriptionToken:
    | RefreshProjectRealtimeSubscriptionTokenResponseBody["token"]
    | null;
  isGeneratingNewProject: boolean;
  newProjectId: string;
  isReadonly: boolean;
  isRealtimeConnected: boolean;
};

type ImagineStoreActions = {
  setProject: (project: GetProjectResponseBody | null) => void;
  setProjectId: (projectId: string | null) => void;
  setIsReadonly: (isReadonly: boolean) => void;
  setProjectRealtimeSubscriptionToken: (
    token: RefreshProjectRealtimeSubscriptionTokenResponseBody["token"] | null,
  ) => void;
  setIsRealtimeConnected: (isRealtimeConnected: boolean) => void;
};

export type ImagineStore = ImagineStoreState & ImagineStoreActions;

const storeWithLenses = withLenses<ImagineStore>((set, get, api) => ({
  projectId: null,
  isGeneratingNewProject: false,
  projectRealtimeSusbcriptionToken: null,
  project: null,
  newProjectId: ID.unique(),
  isReadonly: false,
  isRealtimeConnected: false,
  setIsRealtimeConnected: (isRealtimeConnected) => {
    set({ isRealtimeConnected });
  },
  setProjectId: (projectId) => {
    set({ projectId });
    const currentProject = get().project;
    if (currentProject && currentProject.id !== projectId) {
      get().setProject(null);
      get().setProjectRealtimeSubscriptionToken(null);
    }
  },
  setProjectRealtimeSubscriptionToken: (token) => {
    set({ projectRealtimeSusbcriptionToken: token });
  },
  setIsReadonly: (isReadonly) => {
    set({ isReadonly });
  },
  setProject: (project) => {
    const currentProjectId = get().project?.id;
    const storeProjectId = get().projectId;

    // Check if it's the same project (by project object ID OR store projectId)
    if (
      project?.id &&
      (project.id === currentProjectId || project.id === storeProjectId)
    ) {
      // Same project, just update the project data without resetting slices
      set({
        project,
        projectId: project.id,
      });
      return;
    }

    set({
      project,
      projectId: project?.id ?? null,
    });
    if (project) {
      get().chat.reset();
    } else {
      set({ newProjectId: ID.unique() });
    }

    set({ isRealtimeConnected: false });

    imagineConfigStore.getState().setError(null);

    // Reset slices
    get().previewState.reset();
    get().workspaceState.reset();
    get().sandboxState.reset();
    get().codeEditor.reset();
    get().promptContext.reset();
    get().ui.reset();
    get().billing.reset();

    // Reset sandbox
    get().sandboxState.reset();
    get().consoleLogs.reset();
    get().networkLogs.reset();
  },
  ...slices,
}));

const baseStore = createSelectors(
  create(
    devtools(storeWithLenses, {
      enabled: import.meta.env.VITE_PUBLIC_DEVTOOLS_ENABLED === "true",
    }),
  ),
);

export const imagineStore: StoreWithNestedSlices<
  typeof baseStore,
  keyof typeof slices
> = createNestedSliceSelectors(
  baseStore,
  Object.keys(slices) as Array<keyof typeof slices>,
);
